import { supabase } from '@/integrations/supabase/client';
import type { UserStatus } from '@/integrations/supabase/types';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { sessionService } from '@/services/sessionService';

// Re-export UserStatus for convenience
export type { UserStatus };

export interface UserStatusInfo {
  id: string;
  status: UserStatus;
  last_seen: string;
  is_online?: boolean;
}

export interface StatusUpdateEvent {
  user_id: string;
  status: UserStatus;
  timestamp: string;
}

class StatusService {
  private statusChannel: RealtimeChannel | null = null;
  private statusListeners: Map<string, Set<(status: UserStatusInfo) => void>> = new Map();
  private currentUserId: string | null = null;
  private visibilityCleanup: (() => void) | null = null;
  private focusCleanup: (() => void) | null = null;
  private browserCloseCleanup: (() => void) | null = null;
  private manualStatus: UserStatus | null = null; // Track manually set status to respect user intent
  private isInitialized: boolean = false; // Prevent multiple initialization
  private offlineQueue: Array<{ status: UserStatus; useKeepalive: boolean; isManual: boolean }> = [];
  private lastStatusUpdate: number = 0; // Track when status was last updated manually // Queue for offline updates
  private onlineListener: (() => void) | null = null; // Listener for online events

  constructor() {
    // Initialize realtime updates will be called after user is set
  }

  private initializeRealtimeUpdates() {
    if (this.statusChannel) {
      // Properly unsubscribe before removing to prevent leakage
      this.statusChannel.unsubscribe();
      supabase.removeChannel(this.statusChannel);
      this.statusChannel = null;
    }

    // Listen for profile status changes with a unique channel name to prevent conflicts
    const channelName = `user_status_updates_${Date.now()}`;
    this.statusChannel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles'
        },
        (payload) => {
          // Notify listeners for any user whose status changes
          // notifyStatusListeners will check if there's a listener for that userId
          this.notifyStatusListeners(payload.new as UserStatusInfo);
        }
      )
      .subscribe();
  }

  // Set up user tracking for current session
  async initializeUser(userId: string, setInitialStatus: boolean = true) {
    // Prevent multiple initialization
    if (this.isInitialized && this.currentUserId === userId) {
      console.log('StatusService already initialized for this user');
      return;
    }

    // If initializing for a different user, clean up first
    if (this.isInitialized && this.currentUserId !== userId) {
      console.log('Cleaning up previous user initialization');
      this.partialCleanup();
    }

    this.currentUserId = userId;
    this.isInitialized = true;

    try {
      // Create or update session for this device
      await sessionService.createSession(userId);

      // Verify session is valid before proceeding
      const isValid = await sessionService.isSessionValid();
      if (!isValid) {
        console.error('Failed to create valid session - aborting initialization');
        this.partialCleanup();
        return;
      }

      // Initialize realtime updates after user is set
      this.initializeRealtimeUpdates();

      // Set initial online status only if requested
      if (setInitialStatus) {
        await this.updateStatus('online');
      }

      // Set up tab visibility tracking
      this.visibilityCleanup = this.setupVisibilityTracking();
      this.focusCleanup = this.setupWindowFocusTracking();

      // Set initial status based on page state if no manual status is set
      if (!setInitialStatus && !document.hidden) {
        // Page is visible - check if we should set to online
        // Only override if no manual status is set OR manual status is 'online'
        // Don't override 'away', 'busy', or 'invisible' as these are explicit user choices
        if (!this.hasManualStatus() || this.manualStatus === 'online') {
          this.updateStatus('online').catch(console.error);
        }
      }

      // Set up periodic heartbeat to keep status fresh
      this.startHeartbeat();

      // Set up browser close detection
      this.browserCloseCleanup = this.setupBrowserCloseDetection();

      // Set up online event listener for processing queued updates
      this.onlineListener = this.setupOnlineListener();

      // Initialize session service components
      sessionService.startUserServices();
    } catch (error) {
      console.error('Error initializing user in statusService:', error);
      this.partialCleanup();
      throw error;
    }
  }

  // Set up user tracking without changing status (for existing users)
  setupUserTracking(userId: string, startHeartbeat: boolean = true) {
    // Prevent multiple initialization
    if (this.isInitialized && this.currentUserId === userId) {
      return;
    }

    // If initializing for a different user, clean up first
    if (this.isInitialized && this.currentUserId !== userId) {
      this.partialCleanup();
    }

    this.currentUserId = userId;
    this.isInitialized = true;
    this.initializeRealtimeUpdates();

    // Set up tab visibility tracking
    this.visibilityCleanup = this.setupVisibilityTracking();
    this.focusCleanup = this.setupWindowFocusTracking();

    // Set up periodic heartbeat to keep status fresh (optional)
    if (startHeartbeat) {
      this.startHeartbeat();
    }

    // Set up browser close detection
    this.browserCloseCleanup = this.setupBrowserCloseDetection();

    // Set up online event listener for processing queued updates
    this.onlineListener = this.setupOnlineListener();
  }

  private setupVisibilityTracking() {
    let hiddenTime: number | null = null;
    let offlineTimeout: NodeJS.Timeout | null = null;

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        // Tab became hidden (tab is not visible - user switched tabs, minimized, etc.)
        hiddenTime = Date.now();
        // Only set to away if user is currently online (don't override busy/away/invisible)
        if (this.manualStatus === 'online' || !this.hasManualStatus()) {
          await this.updateStatus('away');
        }

        // Set a timeout to mark offline after 5 minutes of being hidden
        if (offlineTimeout) {
          clearTimeout(offlineTimeout);
        }
        offlineTimeout = setTimeout(async () => {
          // Only set offline if still hidden
          if (document.hidden) {
            await this.updateStatus('offline');
          }
        }, 5 * 60 * 1000); // 5 minutes
      } else {
        // Tab became visible again (tab is now visible)
        // Clear any pending offline timeout
        if (offlineTimeout) {
          clearTimeout(offlineTimeout);
          offlineTimeout = null;
        }

        // If tab is visible, set to online (focus will be checked separately)
        // Only set to online if no manual status is set OR manual status is 'online'
        // Don't override 'away', 'busy', or 'invisible' as these are explicit user choices
        if (!this.hasManualStatus() || this.manualStatus === 'online') {
          await this.updateStatus('online');
        }
        // If tab is visible but no focus, focus tracking will handle it
        hiddenTime = null;
      }
    };

    // Check initial state - if page is already visible, set to online
    // Only if no manual status is set OR manual status is 'online'
    // Don't override 'away', 'busy', or 'invisible' as these are explicit user choices
    if (!document.hidden && (!this.hasManualStatus() || this.manualStatus === 'online')) {
      this.updateStatus('online').catch(console.error);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (offlineTimeout) {
        clearTimeout(offlineTimeout);
      }
    };
  }

  private setupWindowFocusTracking() {
    let focusTimeout: NodeJS.Timeout | null = null;

    const handleFocus = async () => {
      if (focusTimeout) {
        clearTimeout(focusTimeout);
        focusTimeout = null;
      }
      // Only set to online if no manual status is set OR manual status is 'online'
      // Don't override 'away', 'busy', or 'invisible' as these are explicit user choices
      if (!this.hasManualStatus() || this.manualStatus === 'online') {
        await this.updateStatus('online');
      }
    };

    const handleBlur = () => {
      focusTimeout = setTimeout(async () => {
        // Only set to away if user is currently online (don't override busy/away/invisible)
        if (this.manualStatus === 'online' || !this.hasManualStatus()) {
          await this.updateStatus('away');
        }
      }, 30000); // 30 seconds of no focus
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // Cleanup function
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      if (focusTimeout) {
        clearTimeout(focusTimeout);
      }
    };
  }

  private setupBrowserCloseDetection() {
    // Handle page unload - set status to offline
    // We use multiple strategies for maximum reliability:
    // 1. beforeunload - fires when page is about to unload
    // 2. pagehide - more reliable, fires when page is being hidden/unloaded

    const updateToOffline = () => {
      // Use statusService with keepalive flag to ensure the request completes
      // even after the page unloads. This uses fetch with keepalive: true
      // which allows the browser to complete the request after page termination.
      this.updateStatus('offline', true).catch(() => {
        // Silently fail - we tried our best
      });
    };

    // Strategy 1: beforeunload - fires first, but may not complete async operations
    const handleBeforeUnload = () => {
      updateToOffline();
    };

    // Strategy 2: pagehide - more reliable than beforeunload, fires when page is being unloaded
    // This event is more reliable for detecting page closure
    const handlePageHide = (e: PageTransitionEvent) => {
      // If page is being unloaded (not just hidden), set to offline
      if (e.persisted === false) {
        // Page is not being cached (e.g., back/forward cache), so it's being closed
        updateToOffline();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }

  private setupOnlineListener() {
    // Remove existing listener if present
    if (this.onlineListener) {
      this.onlineListener();
      this.onlineListener = null;
    }
    
    const handleOnline = () => {
      console.log('Connection restored - processing offline queue');
      this.processOfflineQueue();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }

  private heartbeatInterval: NodeJS.Timeout | null = null;
  private staleCheckInterval: NodeJS.Timeout | null = null;

  private startHeartbeat() {
    // Prevent duplicate intervals
    if (this.heartbeatInterval || this.staleCheckInterval) {
      return;
    }
    
    // Update timestamp every 30 seconds to show activity (keeps user online)
    this.heartbeatInterval = setInterval(async () => {
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        await this.updateHeartbeat();
      }
    }, 30000);

    // Also check for stale online users every 5 minutes and mark them offline
    this.staleCheckInterval = setInterval(async () => {
      await this.checkAndMarkStaleUsersOffline();
    }, 5 * 60 * 1000); // 5 minutes
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.staleCheckInterval) {
      clearInterval(this.staleCheckInterval);
      this.staleCheckInterval = null;
    }
  }

  // Check for users who have been online for too long without activity and mark them offline
  private async checkAndMarkStaleUsersOffline(): Promise<void> {
    try {
      // Find users who are online but haven't updated their status in the last 10 minutes
      // This is a fallback mechanism when session management isn't available
      const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();

      const { data: staleUsers, error } = await supabase
        .from('profiles')
        .select('id, status, updated_at')
        .in('status', ['online', 'away', 'busy'])
        .lt('updated_at', tenMinutesAgo);

      if (error || !staleUsers) {
        return;
      }

      for (const user of staleUsers) {
        try {
          // Mark as offline
          await supabase
            .from('profiles')
            .update({
              status: 'offline',
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);
        } catch (updateError) {
          console.error(`Failed to mark stale user ${user.id} offline:`, updateError);
        }
      }
    } catch (error) {
      console.error('Error checking for stale users:', error);
    }
  }

  // Set manual status (user explicitly chose this status)
  setManualStatus(status: UserStatus | null): void {
    this.manualStatus = status;
    this.lastStatusUpdate = Date.now();
  }

  // Check if user has a manual status that should override automatic changes
  hasManualStatus(): boolean {
    return this.manualStatus !== null;
  }

  // Get current manual status
  getManualStatus(): UserStatus | null {
    return this.manualStatus;
  }

  // Update just the timestamp without changing status (for heartbeat)
  async updateHeartbeat(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const updatedAt = new Date().toISOString();

      const { error } = await supabase
        .from('profiles')
        .update({
          updated_at: updatedAt
        })
        .eq('id', this.currentUserId);

      if (error) {
        console.error('Error updating heartbeat:', error);
        return;
      }

      // Notify listeners with current status but updated timestamp
      const currentStatus = await this.getUserStatus(this.currentUserId);
      if (currentStatus) {
        this.notifyStatusListeners({
          id: this.currentUserId,
          status: currentStatus.status,
          updated_at: updatedAt
        });
      }
    } catch (error) {
      console.error('Error updating heartbeat:', error);
    }
  }

  // Process queued offline updates
  private async processOfflineQueue(): Promise<void> {
    if (this.offlineQueue.length === 0 || !navigator.onLine) return;

    const queueCopy = [...this.offlineQueue];
    this.offlineQueue = [];

    for (const update of queueCopy) {
      try {
        const success = await this.updateStatus(update.status, update.useKeepalive, update.isManual, this.currentUserId || undefined);
        if (!success) {
          console.log('Status update queued or failed, will retry later');
          // Re-queue failed updates
          this.offlineQueue.unshift(update);
          break; // Stop processing on first failure
        }
      } catch (error) {
        console.error('Failed to process queued status update:', error);
        // Re-queue failed updates
        this.offlineQueue.unshift(update);
        break; // Stop processing on first failure
      }
    }
  }

  // Update user status in database
  async updateStatus(status: UserStatus, useKeepalive: boolean = false, isManual: boolean = false, userId?: string): Promise<boolean> {
    const targetUserId = userId || this.currentUserId;
    if (!targetUserId) return false;

    // Validate session if this is for the current user
    if (!userId && !(await sessionService.isSessionValid())) {
      console.warn('Session invalid - status update blocked');
      return false;
    }

    // If offline, queue the update
    if (!navigator.onLine && !useKeepalive) {
      console.log('Offline - queuing status update:', status);
      this.offlineQueue.push({ status, useKeepalive, isManual });
      return false;
    }

    try {
      const updatedAt = new Date().toISOString();

      // If keepalive is requested (e.g., from beforeunload), use fetch directly
      if (useKeepalive) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

            // Use fetch with keepalive to ensure request completes even after page unload
            fetch(`${supabaseUrl}/rest/v1/profiles?id=eq.${targetUserId}`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'apikey': supabaseKey,
                'Authorization': `Bearer ${session.access_token}`,
                'Prefer': 'return=minimal'
              },
              body: JSON.stringify({
                status,
                updated_at: updatedAt
              }),
              keepalive: true // Critical: allows request to complete after page unload
            }).catch(() => {
              // Silently fail - we tried our best
            });
          }
        } catch (error) {
          // Silently fail - we tried our best
        }
        return true; // Assume success for keepalive
      }

      // Normal update using Supabase client
      const { error } = await supabase
        .from('profiles')
        .update({
          status,
          updated_at: updatedAt
        })
        .eq('id', targetUserId);

      if (error) {
        console.error('Error updating status:', error);
        throw new Error(`Failed to update status: ${error.message}`);
      }

      // Immediately notify listeners for this user's status change
      // This ensures instant updates before the realtime subscription propagates
      // Pass data in the format notifyStatusListeners expects (with updated_at)
      this.notifyStatusListeners({
        id: targetUserId,
        status,
        updated_at: updatedAt
      });

      return true;
    } catch (error) {
      console.error('Error updating status:', error);
      throw error; // Re-throw so callers know it failed
    }
  }

  // Get current status of a user
  async getUserStatus(userId: string): Promise<UserStatusInfo | null> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, status, updated_at')
        .eq('id', userId)
        .single();

      if (error || !data) return null;

      const lastSeen = new Date(data.updated_at);
      const now = new Date();
      const timeDiff = now.getTime() - lastSeen.getTime();

      // Consider user offline if no update in last 5 minutes or if status is invisible
      const isOnline = data.status !== 'offline' && data.status !== 'invisible' && timeDiff < 5 * 60 * 1000;

      return {
        id: data.id,
        status: data.status || 'offline',
        last_seen: data.updated_at,
        is_online: isOnline
      };
    } catch (error) {
      console.error('Error fetching user status:', error);
      return null;
    }
  }

  // Get statuses for multiple users
  async getUsersStatuses(userIds: string[]): Promise<UserStatusInfo[]> {
    if (userIds.length === 0) return [];

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, status, updated_at')
        .in('id', userIds);

      if (error || !data) return [];

      return data.map(user => {
        const lastSeen = new Date(user.updated_at);
        const now = new Date();
        const timeDiff = now.getTime() - lastSeen.getTime();
        const isOnline = user.status !== 'offline' && user.status !== 'invisible' && timeDiff < 5 * 60 * 1000;

        return {
          id: user.id,
          status: user.status || 'offline',
          last_seen: user.updated_at,
          is_online: isOnline
        };
      });
    } catch (error) {
      console.error('Error fetching users statuses:', error);
      return [];
    }
  }

  // Listen for status updates of specific users
  onStatusUpdate(userId: string, callback: (status: UserStatusInfo) => void) {
    // Support multiple listeners per userId
    if (!this.statusListeners.has(userId)) {
      this.statusListeners.set(userId, new Set());
    }
    this.statusListeners.get(userId)!.add(callback);

    // Return cleanup function
    return () => {
      const listeners = this.statusListeners.get(userId);
      if (listeners) {
        listeners.delete(callback);
        // Clean up empty sets
        if (listeners.size === 0) {
          this.statusListeners.delete(userId);
        }
      }
    };
  }

  private notifyStatusListeners(userData: any) {
    const lastSeen = userData.updated_at ? new Date(userData.updated_at) : new Date();
    const now = new Date();
    const timeDiff = now.getTime() - lastSeen.getTime();

    // Calculate is_online consistently with getUserStatus
    const isOnline = userData.status !== 'offline' &&
      userData.status !== 'invisible' &&
      timeDiff < 5 * 60 * 1000; // Within 5 minutes

    const statusInfo: UserStatusInfo = {
      id: userData.id,
      status: userData.status || 'offline',
      last_seen: userData.updated_at || new Date().toISOString(),
      is_online: isOnline
    };

    // Notify all listeners for this userId
    const listeners = this.statusListeners.get(userData.id);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(statusInfo);
        } catch (error) {
          console.error('Error in status listener:', error);
        }
      });
    }
  }

  // Partial cleanup - removes listeners but doesn't set offline (for component unmount)
  partialCleanup() {
    this.stopHeartbeat();
    this.currentUserId = null;
    this.isInitialized = false;

    if (this.statusChannel) {
      // Properly unsubscribe before removing to prevent leakage
      this.statusChannel.unsubscribe();
      supabase.removeChannel(this.statusChannel);
      this.statusChannel = null;
    }

    // Clean up event listeners
    if (this.visibilityCleanup) {
      this.visibilityCleanup();
      this.visibilityCleanup = null;
    }

    if (this.focusCleanup) {
      this.focusCleanup();
      this.focusCleanup = null;
    }

    if (this.browserCloseCleanup) {
      this.browserCloseCleanup();
      this.browserCloseCleanup = null;
    }

    if (this.onlineListener) {
      this.onlineListener();
      this.onlineListener = null;
    }

    this.statusListeners.clear();
    this.manualStatus = null; // Clear manual status on cleanup
    this.offlineQueue = []; // Clear offline queue on cleanup
  }

  // Get preferred status from database
  async getPreferredStatus(userId: string): Promise<UserStatus | null> {
    return await sessionService.getPreferredStatus(userId);
  }

  // Save preferred status to database
  async savePreferredStatus(userId: string, status: UserStatus): Promise<void> {
    // If this is a manual status change, save it as preferred
    if (this.hasManualStatus()) {
      await sessionService.savePreferredStatus(userId, status);
    }
  }

  // Clean up when user logs out or component unmounts
  async cleanup() {
    if (this.currentUserId) {
      await this.updateStatus('offline');
    }

    // Clean up session
    await sessionService.cleanup();

    this.partialCleanup();
  }

  // Get status color for UI display
  getStatusColor(status: UserStatus): string {
    switch (status) {
      case 'online':
        return '#10b981'; // green
      case 'away':
        return '#f59e0b'; // yellow
      case 'busy':
        return '#ef4444'; // red
      case 'invisible':
        return '#6b7280'; // gray
      case 'offline':
      default:
        return '#9ca3af'; // light gray
    }
  }

  // Get status label for display
  getStatusLabel(status: UserStatus): string {
    switch (status) {
      case 'online':
        return 'Online';
      case 'away':
        return 'Away';
      case 'busy':
        return 'Busy';
      case 'invisible':
        return 'Invisible';
      case 'offline':
      default:
        return 'Offline';
    }
  }
}

// Export singleton instance
export const statusService = new StatusService();
export default statusService;
